// Helpers for interpreting outbound-hook mutation errors.
//
// The backend exposes the machine-readable error kind on `error.subType`
// (a String: "HOOK_REJECTED", "CONFLICT", "FEATURE_DISABLED", ...). `error.code`
// stays an Int for legacy numeric errors. We branch on `subType` first, then
// fall back to matching `error.message` (always present) so the UI still
// degrades gracefully if a given resolver hasn't populated `subType` yet.
// See doc/outbound-hooks-frontend-requirements.md §9.

// The discriminator string, preferring subType and tolerating a stringy code.
export const errorSubType = (error) => {
  if (!error) return ""
  if (error.subType) return String(error.subType)
  // Legacy/interim: some resolvers put the string kind in `code`.
  if (error.code != null && typeof error.code === "string") return error.code
  return ""
}

export const isConflict = (error) => {
  if (!error) return false
  if (errorSubType(error) === "CONFLICT") return true
  const m = (error.message || "").toLowerCase()
  return m.includes("conflict") || m.includes("etag")
}

export const isFeatureDisabled = (error) => {
  if (!error) return false
  if (errorSubType(error) === "FEATURE_DISABLED") return true
  const m = (error.message || "").toLowerCase()
  return m.includes("feature") && m.includes("disabl")
}

export const isHookRuntimeError = (error) => {
  if (!error) return false
  const t = errorSubType(error)
  if (t === "HOOK_REJECTED" || t === "HOOK_TIMEOUT") return true
  const m = (error.message || "").toLowerCase()
  return m.includes("hook")
}

// Human-facing message for any hook mutation error, with a sensible fallback.
export const errorMessage = (error, fallback = "Something went wrong.") =>
  (error && error.message) || fallback

// Known backend codes mapped to friendly text. Because `Error.code` is typed
// `Int` server-side (see §9), a string code like "FEATURE_DISABLED" fails to
// serialize and instead surfaces as a top-level GraphQL error message such as
// `Int cannot represent non-integer value: "FEATURE_DISABLED"`. We detect those
// substrings so the agent sees a clean message instead of the coercion noise.
const KNOWN_CODE_MESSAGES = {
  FEATURE_DISABLED: "Outbound hooks are not enabled for this deployment.",
  FORBIDDEN: "Administrator permission is required.",
}

// Turn a useMutation onError() argument into a clean, human-facing string.
export const networkErrorMessage = (err, fallback = "Something went wrong.") => {
  const raw =
    err?.res?.json?.errors?.[0]?.message || (err && err.message) || (err ? String(err) : "")
  for (const code of Object.keys(KNOWN_CODE_MESSAGES)) {
    if (raw.includes(code)) return KNOWN_CODE_MESSAGES[code]
  }
  return raw || fallback
}
